import { useState, useEffect, useRef, Fragment } from "react";
import { useLocation, Link } from "react-router-dom";
import { CalendarDays, LayoutDashboard, Users, FileText, ClipboardCheck, Receipt, Package, Settings, LogOut, Menu, Globe, Inbox, Share2, Wrench, MailOpen, Landmark, AlertTriangle, UserCheck, Download, HardHat, Smartphone, BookOpen, Eye, Copy, Folder, Briefcase, Calendar, StickyNote, GripVertical, ArrowUpDown, RotateCcw, Check, ChevronDown, Brain, Sun, Moon, Monitor, Droplet, Leaf, Square, Mic, Phone, X, ConciergeBell, FileWarning } from "lucide-react";
import { api } from "@/lib/api";
import { zaehlerAbfrage, pruefeAnstieg } from "@/lib/zaehler";
import { butlerIstAktiv, butlerAktivieren, butlerBeenden, butlerBeobachten } from "@/lib/ablauf";
import { HelpTip } from "@/components/HelpTip";
import { detectAppEnv, ENV_BADGE_CLASSES } from "@/lib/env";
import { colorForUser, initialsOf } from "@/lib/avatarUtils";
import { useTheme } from "@/lib/themeContext";
import { kalenderMenue } from "@/lib/windowSync";
import { toast } from "sonner";
import { GlobalAssistantSheet } from "@/components/GlobalAssistantSheet";

const APP_ENV = detectAppEnv();

// Graupner Butler (15.10.2026, Ralph): „Butler“ ist eine AKTION, kein Seitenaufruf – Klick schaltet die
// Ablaufaufzeichnung ein bzw. aus, ohne Seitenwechsel. Der Schlüssel ist bewusst kein Routenpfad, beginnt
// aber mit "/", weil die gespeicherte Reihenfolge (module_user_prefs) nur solche Einträge annimmt; so bleibt
// der Eintrag wie alle anderen per Sortier-Modus verschiebbar und ändert an der Sortierlogik nichts.
const BUTLER_AKTION = "/butler-aktion";

// Zustand des Butlers für die Navigation (meldet sich bei lib/ablauf.js an, wie die Butler-Leiste)
const useButlerAktiv = () => {
  const [aktiv, setAktiv] = useState(butlerIstAktiv());
  useEffect(() => butlerBeobachten(setAktiv), []);
  return aktiv;
};
const kalenderOeffnen = () => kalenderMenue();   // Regel zentral: lib/windowSync.js kalenderArt()
const butlerUmschalten = () => (butlerIstAktiv() ? butlerBeenden() : butlerAktivieren());

const allNavItems = [
  // Dashboard vorübergehend ausgeblendet (Ralph 17.06.2026) – Route /dashboard + DashboardPage.jsx bleiben erhalten.
  // Zum Reaktivieren einfach die folgende Zeile wieder aktivieren:
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["admin"] },
  { path: "/module/kunden", icon: Users, label: "Kunden", roles: ["admin"], permKey: "modul_kunden" },
  { path: "/module/telefonzentrale", icon: Phone, label: "Telefonzentrale", roles: ["admin"] },
  { path: "/module/mail-inbox", icon: Inbox, label: "Mail-Anfragen", roles: ["admin"], permKey: "modul_mail_anfragen" },
  { path: "/module/duplikate", icon: Copy, label: "Duplikate", roles: ["admin"], parentPath: "/settings" },
  { path: "/module/projekte", icon: Folder, label: "Projekte", roles: ["admin"], permKey: "modul_projekte" },
  { path: "/module/aufgaben", icon: Briefcase, label: "Aufgaben", roles: ["admin", "mitarbeiter", "buchhaltung"], permKey: "modul_aufgaben" },
  { path: "/kalender", action: "kalender", icon: CalendarDays, label: "Kalender", roles: ["admin"], permKey: "modul_termine" },
  { path: "/module/assistent", icon: Brain, label: "Mein Assistent", roles: ["admin"] },
  { path: "/einsaetze", icon: Wrench, label: "Einsätze", roles: ["admin"], permKey: "modul_einsaetze" },
  { path: "/module/artikel", icon: Package, label: "Artikel & Leistungen", roles: ["admin"], parentPath: "/settings" },
  { path: "/module/dokumente", icon: FileText, label: "Dokumente", roles: ["admin"], permKey: "modul_dokumente" },
  { path: "/module/dokumente-v6", icon: FileText, label: "Dokumente v6 (Sicherung)", roles: ["admin"], variant: "deprecated", hideByDefault: true },
  { path: "/dokumente-v2", icon: FileText, label: "Dokumente v2 (defekt)", roles: ["admin"], variant: "deprecated", hideByDefault: true },
  { path: "/module/textvorlagen", icon: FileText, label: "Textvorlagen", roles: ["admin"], parentPath: "/settings" },
  { path: "/module/kundenportal", icon: Share2, label: "Kundenportal", roles: ["admin"], permKey: "modul_kundenportale" },
  // ACHTUNG: Dies ist das ALTE Kundenportal (routes_legacy.py / /portals).
  // Nicht anfassen, nicht löschen, nicht mit module_portal_wizard verwechseln.
  // Das neue Kundenportal-Modul ist: module_portal_wizard / /kundenportal
  // Stand: 21.06.2026 — Ralph Graupner
  { path: "/portals", icon: Globe, label: "Kundenportale (ALT)", roles: ["admin"], permKey: "modul_kundenportale", variant: "deprecated", hideByDefault: true },
  { path: "/portals-klon", icon: Globe, label: "Kundenportale (Arbeitskopie)", roles: ["admin"], hideByDefault: true },
  { path: "/monteur", icon: HardHat, label: "Monteur-App", roles: ["admin", "mitarbeiter", "buchhaltung"], permKey: "modul_monteur_app" },
  { path: "/handy-zugang", icon: Smartphone, label: "Handy-Zugang", roles: ["admin"], parentPath: "/settings" },
  { path: "/wissen", icon: BookOpen, label: "Wissen & Tipps", roles: ["admin"], parentPath: "/settings" },
  { path: "/buchhaltung", icon: Landmark, label: "Buchhaltung", roles: ["admin", "buchhaltung"], permKey: "modul_buchhaltung" },
  { path: "/invoices", icon: Receipt, label: "Rechnungen", roles: ["admin", "buchhaltung"], hideByDefault: true },
  { path: "/rechnungen-v2", icon: Receipt, label: "Rechnungen (Neu)", roles: ["admin"], featureFlag: "rechnungen_v2", hideByDefault: true },
  { path: "/email", icon: MailOpen, label: "E-Mail", roles: ["admin"], featureFlag: "email_module_enabled" },
  // Graupner Butler (15.10.2026): direkt vor „Einstellungen“
  { path: BUTLER_AKTION, action: "butler", icon: ConciergeBell, label: "Butler", roles: ["admin", "mitarbeiter", "buchhaltung", "monteur"] },
  { path: "/butler/fehlerakten", icon: FileWarning, label: "Fehlerakten", roles: ["admin"] },
  { path: "/settings", icon: Settings, label: "Einstellungen", roles: ["admin"], hasChildren: true, permKey: "modul_einstellungen" },
];

const getUserRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user && typeof user === "object") return user.role || "mitarbeiter";
    return "admin";
  } catch { return "admin"; }
};

const getUserBerechtigungen = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user && typeof user === "object" && user.berechtigungen && typeof user.berechtigungen === "object") {
      return user.berechtigungen;
    }
    return null; // null = keine Daten vorhanden → nicht filtern (Fallback admin-friendly)
  } catch { return null; }
};

const getFilteredNavItems = () => {
  const role = getUserRole();
  const perms = getUserBerechtigungen();
  let flags = {};
  try { flags = JSON.parse(localStorage.getItem("feature_flags") || "{}"); } catch { /* ignore */ }
  let showLegacy = false;
  try { showLegacy = localStorage.getItem("show_legacy_modules") === "1"; } catch { /* ignore */ }
  return allNavItems.filter(item => {
    if (!item.roles.includes(role)) return false;
    if (item.featureFlag && !flags[item.featureFlag]) return false;
    if (item.hideByDefault && !showLegacy) return false;
    // NOTFALL-FIX 17.05.2026: Berechtigungs-Filter deaktiviert (verbarg auf Live alle Module).
    // Backend speichert Berechtigungen weiterhin in DB, UI in BenutzerTab funktioniert.
    // Wieder-Aktivierung erst nach Fix der Filter-Logik.
    // if (item.permKey && perms !== null && Object.keys(perms).length > 0) {
    //   if (perms[item.permKey] !== true) return false;
    // }
    return true;
  });
};

/** Kinder-Items (parentPath gesetzt) werden aus der Top-Liste herausgefiltert
 *  und stattdessen als Kinder unter ihrem Parent angezeigt. */
const getChildren = (allItems, parentPath) => allItems.filter(i => i.parentPath === parentPath);

const ThemeToggle = ({ variant = "sidebar" }) => {
  const { theme, cycleTheme } = useTheme();
  const meta = {
    light:        { Icon: Sun,     label: "Hell",        next: "Dunkel" },
    dark:         { Icon: Moon,    label: "Dunkel",      next: "Dunkelblau" },
    "dark-blue":  { Icon: Droplet, label: "Dunkelblau",  next: "Grün" },
    "dark-green": { Icon: Leaf,    label: "Grün",        next: "Grau" },
    gray:         { Icon: Square,  label: "Grau",        next: "Automatisch" },
    system:       { Icon: Monitor, label: "Automatisch", next: "Hell" },
  };
  const cur = meta[theme] || meta.system;
  const Icon = cur.Icon;
  if (variant === "sidebar-compact") {
    return (
      <button
        onClick={cycleTheme}
        data-testid="btn-theme-toggle"
        title={`Design: ${cur.label} · Klick: ${cur.next}`}
        aria-label={`Design wechseln (aktuell: ${cur.label})`}
        className="w-9 h-9 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-smooth"
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  }
  return (
    <button
      onClick={cycleTheme}
      data-testid="btn-theme-toggle"
      title={`Aktuell: ${cur.label} · Klick: ${cur.next}`}
      className={
        variant === "mobile"
          ? "flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-smooth"
          : "flex items-center gap-3 px-4 py-2 w-full text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-smooth"
      }
    >
      <Icon className="w-4 h-4" />
      <span>Design: {cur.label}</span>
    </button>
  );
};

const Sidebar = ({ onLogout }) => {
  const location = useLocation();
  const allFilteredItems = getFilteredNavItems();
  const baseNavItems = allFilteredItems.filter(i => !i.parentPath); // ohne Kinder-Einträge
  const role = getUserRole();
  const username = (() => { try { const u = JSON.parse(localStorage.getItem("user") || "null"); return typeof u === "object" ? u.username : u; } catch { return ""; } })();
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantSnoozeContext, setAssistantSnoozeContext] = useState(null);
  const butlerAktiv = useButlerAktiv();
  const [unreadCounts, setUnreadCounts] = useState({ email: 0, portal: 0, termine_go: 0, assistent: 0, mail_anfragen: 0, projekte_neu: 0, aufgaben_offen: 0, kundenportal_neu: 0 });

  // URL-Param-Trigger: ?assistant=snooze&type=…&id=…&token=… öffnet das Sheet im Snooze-Modus
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("assistant") === "snooze") {
        const ctx = {
          entity_type: params.get("type"),
          entity_id: params.get("id"),
          push_token: params.get("token"),
        };
        if (ctx.entity_type && ctx.entity_id && ctx.push_token) {
          setAssistantSnoozeContext(ctx);
          setAssistantOpen(true);
          window.history.replaceState({}, "", window.location.pathname);
        }
      }
    } catch { /* ignore */ }
  }, []);
  const prevEmailRef = useRef(null);
  const letzteZaehlerRef = useRef({});  // letzter bekannter Wert je Abfrage (Fehlerrunde behält ihn)
  const prevKundenportalRef = useRef(null);
  const prevMailAnfragenRef = useRef(null);
  const [banners, setBanners] = useState([]);
  const addBanner = (key, message, link) => {
    setBanners((prev) => [...prev.filter((b) => b.key !== key), { key, message, link, id: Date.now() }]);
  };
  const dismissBanner = (id) => {
    setBanners((prev) => prev.filter((b) => b.id !== id));
  };
  const [openedParents, setOpenedParents] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("nav_opened_parents") || "[]")); } catch { return new Set(); }
  });
  const toggleParent = (path) => {
    setOpenedParents(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      try { localStorage.setItem("nav_opened_parents", JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  };

  // ----- Sidebar-Reihenfolge (per User in DB gespeichert) -----
  const [sortMode, setSortMode] = useState(false);
  const [customOrder, setCustomOrder] = useState([]);  // Pfade in gewünschter Reihenfolge
  const [draggedPath, setDraggedPath] = useState(null);
  const [dragOverPath, setDragOverPath] = useState(null);

  // Initial-Load der User-Prefs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/module-user-prefs/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (Array.isArray(data.sidebar_order)) setCustomOrder(data.sidebar_order);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sortierte Sidebar-Items (custom_order zuerst, Rest in Original-Reihenfolge ans Ende)
  const navItems = (() => {
    if (!customOrder.length) return baseNavItems;
    const byPath = new Map(baseNavItems.map(i => [i.path, i]));
    const ordered = customOrder.map(p => byPath.get(p)).filter(Boolean);
    const orderedSet = new Set(ordered.map(i => i.path));
    const rest = baseNavItems.filter(i => !orderedSet.has(i.path));
    // Neue Items (rest) werden direkt nach ihrem natürlichen Vorgänger in baseNavItems
    // eingefügt, statt ganz unten zu landen. So sieht der User sie sofort.
    if (rest.length === 0) return ordered;
    const out = [...ordered];
    for (const newItem of rest) {
      const origIdx = baseNavItems.findIndex(i => i.path === newItem.path);
      // Finde den nächsten Nachbarn aus baseNavItems der schon in out ist
      let inserted = false;
      for (let k = origIdx - 1; k >= 0; k--) {
        const prevPath = baseNavItems[k].path;
        const pos = out.findIndex(i => i.path === prevPath);
        if (pos !== -1) {
          out.splice(pos + 1, 0, newItem);
          inserted = true;
          break;
        }
      }
      if (!inserted) out.unshift(newItem);
    }
    return out;
  })();

  const persistOrder = async (newOrder) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/module-user-prefs/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ sidebar_order: newOrder }),
      });
    } catch { /* ignore – UI bleibt trotzdem aktuell */ }
  };

  const handleDragStart = (path) => (e) => {
    if (!sortMode) return;
    setDraggedPath(path);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (path) => (e) => {
    if (!sortMode || !draggedPath) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverPath(path);
  };
  const handleDragLeave = () => setDragOverPath(null);
  const handleDrop = (targetPath) => (e) => {
    if (!sortMode || !draggedPath) return;
    e.preventDefault();
    if (draggedPath === targetPath) { setDraggedPath(null); setDragOverPath(null); return; }
    const order = navItems.map(i => i.path);
    const fromIdx = order.indexOf(draggedPath);
    const toIdx = order.indexOf(targetPath);
    if (fromIdx === -1 || toIdx === -1) return;
    order.splice(fromIdx, 1);
    order.splice(toIdx, 0, draggedPath);
    setCustomOrder(order);
    setDraggedPath(null);
    setDragOverPath(null);
    persistOrder(order);
  };

  const resetOrder = async () => {
    if (!window.confirm("Standard-Reihenfolge wiederherstellen?")) return;
    setCustomOrder([]);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/module-user-prefs/me`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
    } catch { /* ignore */ }
  };
  // ----- Ende Sidebar-Reihenfolge -----

  // Poll fuer ungelesene E-Mails und Portal-Aktivitaeten
  useEffect(() => {
    let cancelled = false;
    // Feature-Flag pruefen: wenn E-Mail-Modul aus -> KEINE IMAP-Calls im Hintergrund
    let emailModuleEnabled = false;
    try {
      const flags = JSON.parse(localStorage.getItem("feature_flags") || "{}");
      emailModuleEnabled = !!flags.email_module_enabled;
    } catch { /* ignore */ }

    const fetchCounts = async () => {
      try {
        const merker = letzteZaehlerRef.current;
        const pfade = [
          "/portals/unread-count",
          "/module-termine/wartet-auf-go",
          "/anfragen/count-neu",
          "/module-mail-inbox/stats",
          "/module-projekte/count-neu",
          "/module-aufgaben/count-offen",
          "/kundenportal/admin/unread-count",
        ];
        if (emailModuleEnabled) pfade.unshift("/imap/inbox/stats");
        // Fehlgeschlagene Abfrage behält den letzten Wert (nicht 0) und löst keine Meldung aus
        const results = await Promise.all(pfade.map((pf) => zaehlerAbfrage(api, pf, merker)));
        if (cancelled) return;
        const emailCount = emailModuleEnabled ? (results[0].data?.unread || 0) : 0;
        const portalIdx = emailModuleEnabled ? 1 : 0;
        const portalCount = results[portalIdx].data?.count || 0;
        const terminGoCount = results[portalIdx + 1].data?.count || 0;
        const assistentCount = results[portalIdx + 2]?.data?.count || 0;
        // A-017: offene Eingänge vom Kunden (Reiter „Vom Kunden“) zählen an „Mail-Anfragen“ mit
        const eingaengeOffenCount = results[portalIdx + 6]?.data?.eingaenge_offen || 0;
        const mailAnfragenCount = (results[portalIdx + 3]?.data?.total?.vorschlag || 0) + eingaengeOffenCount;
        const projekteNeuCount = results[portalIdx + 4]?.data?.count || 0;
        const aufgabenOffenCount = results[portalIdx + 5]?.data?.count || 0;
        const kundenportalNeuCount = results[portalIdx + 6]?.data?.count || 0;
        const kundenportalNeuNamen = results[portalIdx + 6]?.data?.namen || [];
        const kundenportalNeuIds = results[portalIdx + 6]?.data?.kunde_ids || [];
        // Sound bei neuer Mail spielen (nur wenn Anzahl wirklich gestiegen, nicht nach Fehlerrunde)
        const okEmail = emailModuleEnabled ? results[0].ok : true;
        const okKp = results[portalIdx + 6].ok;
        const okMa = results[portalIdx + 3].ok && results[portalIdx + 6].ok;
        const anstiegEmail = emailModuleEnabled ? pruefeAnstieg(prevEmailRef.current, emailCount, okEmail) : { melden: false, merker: prevEmailRef.current };
        if (anstiegEmail.melden) {
          try {
            const audio = new Audio("data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=");
            audio.volume = 0.3;
            audio.play().catch(() => {});
          } catch { /* ignore */ }
        }
        prevEmailRef.current = anstiegEmail.merker;
        const anstiegKp = pruefeAnstieg(prevKundenportalRef.current, kundenportalNeuCount, okKp);
        if (anstiegKp.melden) {
          const namenText = kundenportalNeuNamen.slice(0, 3).join(", ") + (kundenportalNeuNamen.length > 3 ? ` und ${kundenportalNeuNamen.length - 3} weitere` : "");
          const kpLink = kundenportalNeuIds.length === 1 ? `/module/kundenportal?kunde_id=${kundenportalNeuIds[0]}` : "/module/kundenportal?filter=neu";
          addBanner("kundenportal", `Neue Kundenportal-Antwort von: ${namenText || "einem Kunden"}`, kpLink);
        }
        prevKundenportalRef.current = anstiegKp.merker;
        const anstiegMa = pruefeAnstieg(prevMailAnfragenRef.current, mailAnfragenCount, okMa);
        if (anstiegMa.melden) {
          addBanner("mail_anfragen", `${anstiegMa.anzahl} neue Mail-Anfrage(n) bzw. Eingänge vom Kunden`, "/module/mail-inbox");
        }
        prevMailAnfragenRef.current = anstiegMa.merker;
        setUnreadCounts({ email: emailCount, portal: portalCount, termine_go: terminGoCount, assistent: assistentCount, mail_anfragen: mailAnfragenCount, projekte_neu: projekteNeuCount, aufgaben_offen: aufgabenOffenCount, kundenportal_neu: kundenportalNeuCount });
      } catch { /* ignore */ }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    const onFocus = () => fetchCounts();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(interval); window.removeEventListener("focus", onFocus); };
  }, []);

  const handleLogoutClick = () => {
    setShowBackupDialog(true);
  };

  const handleBackupAndLogout = async () => {
    setIsCreatingBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backup/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = response.headers.get('Content-Disposition')?.split('filename=')[1] || 
                        `Graupner_Backup_${new Date().toISOString().slice(0,10)}.zip`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Kurze Verzögerung damit Download startet
        setTimeout(() => {
          setShowBackupDialog(false);
          onLogout();
        }, 500);
      } else {
        alert('Backup fehlgeschlagen. Möchten Sie trotzdem abmelden?');
        setShowBackupDialog(false);
        onLogout();
      }
    } catch (error) {
      alert('Backup fehlgeschlagen. Möchten Sie trotzdem abmelden?');
      setShowBackupDialog(false);
      onLogout();
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleLogoutWithoutBackup = () => {
    setShowBackupDialog(false);
    onLogout();
  };

  return (
    <>
      {banners.length > 0 && (
        <div className="fixed top-0 left-0 right-0 z-50 flex flex-col gap-1 p-2">
          {banners.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-3 bg-blue-600 text-white rounded-sm px-4 py-2 shadow-lg" data-testid={`banner-${b.key}`}>
              <Link to={b.link} onClick={() => dismissBanner(b.id)} className="flex-1 font-medium hover:underline">
                🔔 {b.message}
              </Link>
              <button onClick={() => dismissBanner(b.id)} className="p-1 hover:bg-white/20 rounded-sm" data-testid={`banner-dismiss-${b.key}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <aside className="app-sidebar hidden lg:flex fixed left-0 top-0 h-screen w-64 bg-card border-r flex-col z-30">
      <div className="p-6 border-b">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-primary">Graupner Suite 2.0</h1>
              {APP_ENV.kind !== "unknown" && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold tracking-wider ${ENV_BADGE_CLASSES[APP_ENV.color]}`} data-testid="sidebar-env-badge">
                  {APP_ENV.short}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">Tischlerei-Software</p>
          </div>
          <button
            onClick={() => setSortMode(s => !s)}
            className={`p-1.5 rounded-sm border transition-colors flex-shrink-0 ${
              sortMode
                ? "bg-emerald-100 border-emerald-300 text-emerald-700"
                : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
            title={sortMode ? "Sortieren beenden" : "Reihenfolge ändern"}
            data-testid="btn-sidebar-sort-toggle"
          >
            {sortMode ? <Check className="w-4 h-4" /> : <ArrowUpDown className="w-4 h-4" />}
          </button>
        </div>
        {username && (
          <div className="mt-2 flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded-sm" data-testid="user-badge">
            <span className={`w-8 h-8 rounded-full ${colorForUser(username)?.bg || "bg-slate-400"} text-white text-xs font-bold flex items-center justify-center flex-shrink-0 select-none`}>
              {initialsOf(username)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" title={username}>{username}</div>
              <div className="text-xs text-muted-foreground capitalize">{role}</div>
            </div>
          </div>
        )}
        {sortMode && (
          <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded-sm text-xs text-emerald-900" data-testid="sort-mode-hint">
            <strong>Sortier-Modus aktiv.</strong> Einträge per Drag &amp; Drop verschieben.
            <button
              onClick={resetOrder}
              className="mt-1 flex items-center gap-1 text-emerald-700 hover:text-emerald-900 underline"
              data-testid="btn-sidebar-sort-reset"
            >
              <RotateCcw className="w-3 h-3" /> Standard wiederherstellen
            </button>
          </div>
        )}
      </div>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map(({ path, icon: Icon, label, variant, hasChildren, action }) => {
          const children = hasChildren ? getChildren(allFilteredItems, path) : [];
          const isOpen = openedParents.has(path);
          const badgeCount = path === "/email"
            ? unreadCounts.email
            : (path === "/portals"
                ? unreadCounts.portal
                : ((path === "/module/termine" || path === "/kalender")
                    ? unreadCounts.termine_go
                    : (path === "/module/assistent"
                        ? unreadCounts.assistent
                        : (path === "/module/mail-inbox"
                            ? unreadCounts.mail_anfragen
                            : (path === "/module/projekte"
                                ? unreadCounts.projekte_neu
                                : (path === "/module/aufgaben"
                                    ? unreadCounts.aufgaben_offen
                                    : (path === "/module/kundenportal"
                                        ? unreadCounts.kundenportal_neu
                                        : 0)))))));
          const isActive = location.pathname.startsWith(path);
          const hasBadge = badgeCount > 0 && !isActive;
          const helpKey = `nav.${path.split("/").filter(Boolean).pop()}`;
          const isDeprecated = variant === "deprecated";
          const isNew = variant === "new";
          const isSandbox = variant === "sandbox";
          return (
            <Fragment key={path}>
            <div
              draggable={sortMode}
              onDragStart={handleDragStart(path)}
              onDragOver={handleDragOver(path)}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop(path)}
              className={`${sortMode ? "cursor-move" : ""} ${dragOverPath === path && draggedPath !== path ? "ring-2 ring-emerald-400 rounded-sm" : ""} ${draggedPath === path ? "opacity-40" : ""}`}
              data-testid={`nav-row-${path.slice(1)}`}
            >
            <HelpTip id={helpKey} placement="right" block>
              {sortMode ? (
                <div
                  data-testid={`nav-${path.slice(1)}`}
                  className={`relative flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm bg-muted/40 border border-dashed border-muted-foreground/30 select-none`}
                >
                  <GripVertical className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <Icon className="w-4 h-4 text-foreground/70 flex-shrink-0" />
                  <span className="text-foreground/80">{label}</span>
                </div>
              ) : action === "kalender" ? (
              <button
                type="button"
                onClick={kalenderOeffnen}
                title="Kalender (eigenes Fenster oder Hauptfenster, je nach Einstellung)"
                data-testid="nav-kalender"
                className="hidden lg:flex relative w-full items-center gap-2.5 px-3 py-2 text-sm text-left rounded-sm transition-smooth text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{label}</span>
                {hasBadge && <span className="ml-auto min-w-5 h-5 px-1 rounded-full bg-blue-600 text-white text-[11px] flex items-center justify-center" data-testid="nav-kalender-badge">{badgeCount}</span>}
              </button>
              ) : action === "butler" ? (
              <button
                type="button"
                onClick={butlerUmschalten}
                aria-pressed={butlerAktiv}
                title={butlerAktiv ? "Butler beenden (Aufzeichnung stoppen)" : "Butler einschalten – Ablauf zur Fehlersuche aufzeichnen"}
                data-testid="nav-butler"
                className={`relative flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left rounded-sm transition-smooth ${
                  butlerAktiv ? "bg-red-600 text-white font-semibold hover:bg-red-700" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{butlerAktiv ? "Butler beenden" : label}</span>
                {butlerAktiv && <span className="ml-auto h-2 w-2 rounded-full bg-white animate-pulse" aria-hidden="true" />}
              </button>
              ) : (
              <Link
                to={path}
                data-testid={`nav-${path.slice(1)}`}
                className={`relative flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm transition-smooth ${
                  isActive
                    ? (isNew
                        ? "bg-emerald-100 text-emerald-800 border-l-4 border-emerald-600 shadow-sm"
                        : "bg-primary/10 text-primary border-l-2 border-primary")
                    : isDeprecated
                    ? "text-muted-foreground/60 italic hover:bg-muted hover:text-muted-foreground line-through decoration-muted-foreground/40"
                    : isNew
                    ? "text-emerald-800 bg-emerald-50/50 hover:bg-emerald-100/70 border-l-4 border-emerald-300 font-medium"
                    : isSandbox
                    ? "text-amber-700 hover:bg-amber-50 border-l-2 border-dashed border-amber-300"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <div className="relative shrink-0">
                  <Icon className={`w-4 h-4 ${isNew ? "text-emerald-600" : ""}`} />
                  {hasBadge && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-blue-600 text-white text-[9px] font-bold ring-2 ring-background" data-testid={`badge-${path.slice(1)}`}>
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </div>
                <span>{label}</span>
                {isNew && !isActive && (
                  <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white tracking-wider">NEU</span>
                )}
                {isDeprecated && (
                  <span className="ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 tracking-wider">ALT</span>
                )}
                {isSandbox && !isActive && (
                  <span className="ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 tracking-wider border border-amber-300">TEST</span>
                )}
                
                {hasChildren && children.length > 0 && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleParent(path); }}
                    className="ml-auto p-0.5 hover:bg-black/5 rounded-sm"
                    data-testid={`btn-toggle-${path.slice(1)}`}
                    aria-label={isOpen ? "Zuklappen" : "Aufklappen"}
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                )}
              </Link>
              )}
            </HelpTip>
            </div>
            {hasChildren && isOpen && children.length > 0 && (
              <div className="ml-6 mt-0.5 space-y-0.5 border-l border-border pl-2" data-testid={`children-${path.slice(1)}`}>
                {children.map((c) => {
                  const CIcon = c.icon;
                  const cActive = location.pathname.startsWith(c.path);
                  return (
                    <Link
                      key={c.path}
                      to={c.path}
                      className={`flex items-center gap-2 px-2 py-1.5 text-xs rounded-sm transition-smooth ${cActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                      data-testid={`nav-child-${c.path.slice(1)}`}
                    >
                      <CIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{c.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
            </Fragment>
          );
        })}
      </nav>
      <div className="p-4 border-t space-y-1">
      <div className="px-3 pb-2">
        <div className="flex items-center gap-1 mb-1">
          <button
            onClick={() => {
              try {
                const cur = localStorage.getItem("show_legacy_modules") === "1";
                localStorage.setItem("show_legacy_modules", cur ? "0" : "1");
                window.location.reload();
              } catch { /* ignore */ }
            }}
            data-testid="btn-toggle-legacy"
            className="w-9 h-9 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-smooth"
            title={typeof window !== "undefined" && window.localStorage?.getItem("show_legacy_modules") === "1"
              ? "Alt-Module ausblenden"
              : "Alt-Module einblenden (Dokumente alt, Kundenportale alt, Sandbox v3/v4)"}
            aria-label="Alt-Module umschalten"
          >
            <Eye className="w-4 h-4" />
          </button>
          <ThemeToggle variant="sidebar-compact" />
        </div>
        <button
          onClick={() => setAssistantOpen(true)}
          data-testid="btn-sidebar-assistant"
          className="flex items-center gap-3 px-4 py-3 w-full text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-sm transition-smooth"
          title="Mein Assistent — Sprachaufnahme"
        >
          <Mic className="w-5 h-5" />
          <span className="font-medium">Assistent</span>
        </button>
        <button
          onClick={handleLogoutClick}
          data-testid="btn-logout"
          className="flex items-center gap-3 px-4 py-3 w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-sm transition-smooth"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Abmelden</span>
        </button>
      </div>
      </div>
    </aside>

    {/* Backup Dialog */}
    {showBackupDialog && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBackupDialog(false)}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" />
            Backup vor dem Abmelden?
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Möchten Sie vor dem Abmelden ein Backup Ihrer Daten erstellen? Dies sichert alle Anfragen, Kunden, Rechnungen und Einstellungen.
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleBackupAndLogout}
              disabled={isCreatingBackup}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isCreatingBackup ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Erstelle Backup...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Backup & Abmelden
                </>
              )}
            </button>
            <button
              onClick={handleLogoutWithoutBackup}
              disabled={isCreatingBackup}
              className="flex-1 px-4 py-2.5 border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Ohne Backup abmelden
            </button>
          </div>
          <button
            onClick={() => setShowBackupDialog(false)}
            disabled={isCreatingBackup}
            className="mt-3 w-full text-sm text-muted-foreground hover:text-foreground"
          >
            Abbrechen
          </button>
        </div>
      </div>
    )}
    <GlobalAssistantSheet open={assistantOpen} onClose={() => { setAssistantOpen(false); setAssistantSnoozeContext(null); }} snoozeContext={assistantSnoozeContext} />
  </>
  );
};

const MobileNav = ({ onLogout }) => {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantSnoozeContext, setAssistantSnoozeContext] = useState(null);
  const [mailAnfragenNeu, setMailAnfragenNeu] = useState(0);
  const butlerAktiv = useButlerAktiv();
  const navItems = getFilteredNavItems();
  const role = getUserRole();

  // Zähler neue Mail-Anfragen (gleiche Quelle wie am PC), jede Minute und beim Öffnen von "Mehr"
  const handyMerkerRef = useRef({});
  const ladeMailAnfragenNeu = async () => {
    if (role !== "admin") return;
    // Fehlgeschlagene Abfrage behält den letzten Wert (nicht 0)
    const [a, b] = await Promise.all([
      zaehlerAbfrage(api, "/module-mail-inbox/stats", handyMerkerRef.current),
      zaehlerAbfrage(api, "/kundenportal/eingaenge/zaehler", handyMerkerRef.current),  // A-017
    ]);
    if (!a.ok && !b.ok && a.data === null && b.data === null) return;
    setMailAnfragenNeu((a.data?.total?.vorschlag || 0) + (b.data?.offen || 0));
  };
  useEffect(() => {
    ladeMailAnfragenNeu();
    const interval = setInterval(ladeMailAnfragenNeu, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // URL-Param-Trigger (siehe Sidebar) — auch hier nötig, damit auf Mobile das Sheet öffnet
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("assistant") === "snooze") {
        const ctx = {
          entity_type: params.get("type"),
          entity_id: params.get("id"),
          push_token: params.get("token"),
        };
        if (ctx.entity_type && ctx.entity_id && ctx.push_token) {
          setAssistantSnoozeContext(ctx);
          setAssistantOpen(true);
          window.history.replaceState({}, "", window.location.pathname);
        }
      }
    } catch { /* ignore */ }
  }, []);

  const handleLogoutClick = () => {
    setMoreOpen(false);
    setShowBackupDialog(true);
  };

  const handleBackupAndLogout = async () => {
    setIsCreatingBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/backup/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = response.headers.get('Content-Disposition')?.split('filename=')[1] || 
                        `Graupner_Backup_${new Date().toISOString().slice(0,10)}.zip`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setTimeout(() => {
          setShowBackupDialog(false);
          onLogout();
        }, 500);
      } else {
        setShowBackupDialog(false);
        onLogout();
      }
    } catch (error) {
      setShowBackupDialog(false);
      onLogout();
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleLogoutWithoutBackup = () => {
    setShowBackupDialog(false);
    onLogout();
  };

  const mobileTabItems = role === "buchhaltung"
    ? [
        { path: "/buchhaltung", icon: Landmark, label: "Buchhaltung" },
        { path: "/invoices", icon: Receipt, label: "Rechnungen" },
        { path: "/orders", icon: ClipboardCheck, label: "Aufträge" },
        { path: "/module/kunden", icon: Users, label: "Kunden" },
      ]
    : [
        { path: "/module/kalender", icon: Calendar, label: "Kalender" },
        { path: "/module/projekte", icon: Folder, label: "Projekte" },
        { path: "/module/kunden", icon: Users, label: "Kunden" },
        { path: "/module/aufgaben", icon: Briefcase, label: "Aufgaben" },
      ];

  // Handy-Menü "Mehr" (07.10.2026, Ralph): "Mail-Anfragen" ganz oben
  const moreItems = navItems
    .filter(i => !mobileTabItems.find(t => t.path === i.path) && i.action !== "kalender")
    .sort((a, b) => (b.path === "/module/mail-inbox") - (a.path === "/module/mail-inbox"));
  const tabClass = "flex flex-col items-center gap-0.5 py-2 px-1 flex-1 min-w-0";

  return (
    <>
      <header className="app-sidebar lg:hidden fixed top-0 left-0 right-0 h-14 bg-card border-b flex items-center justify-between px-4 z-30">
        <div className="flex items-center gap-1.5 min-w-0">
          <h1 className="text-lg font-bold text-primary">Graupner Suite 2.0</h1>
          {APP_ENV.kind !== "unknown" && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold tracking-wider ${ENV_BADGE_CLASSES[APP_ENV.color]}`} data-testid="mobile-env-badge">
              {APP_ENV.short}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {role === "buchhaltung" && <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-sm font-medium">Buchhaltung</span>}
          <button onClick={() => setMoreOpen(!moreOpen)} className="p-2 hover:bg-muted rounded-sm" data-testid="btn-mobile-menu">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>
      <nav className="app-sidebar lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t flex justify-around items-center z-30 safe-area-bottom" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* 6 Knöpfe teilen sich die Breite (vorher je mind. 64 px = 384 px, bei 360/375 px zu breit) */}
        {mobileTabItems.map(({ path, icon: Icon, label }) => (
          <Link key={path} to={path} data-testid={`mobile-nav-${path.slice(1)}`}
            className={`${tabClass} ${location.pathname.startsWith(path) ? "text-primary" : "text-muted-foreground"}`}>
            <Icon className="w-5 h-5" /><span className="text-[10px] font-medium">{label}</span>
          </Link>
        ))}
        <button onClick={() => setAssistantOpen(true)} data-testid="mobile-nav-assistant"
          className={`${tabClass} text-muted-foreground`}>
          <Mic className="w-5 h-5" /><span className="text-[10px] font-medium">Assistent</span>
        </button>
        <button onClick={() => { if (!moreOpen) ladeMailAnfragenNeu(); setMoreOpen(!moreOpen); }} data-testid="mobile-nav-more"
          className={`${tabClass} ${moreOpen ? "text-primary" : "text-muted-foreground"}`}>
          <span className="relative">
            <Menu className="w-5 h-5" />
            {mailAnfragenNeu > 0 && (
              <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-blue-600 text-white text-[9px] font-bold ring-2 ring-background" data-testid="mobile-badge-more">
                {mailAnfragenNeu > 99 ? "99+" : mailAnfragenNeu}
              </span>
            )}
          </span>
          <span className="text-[10px] font-medium">Mehr</span>
        </button>
      </nav>
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          {/* Höhe begrenzt + eigenes Wischen: vorher wuchs die Liste über den oberen Bildschirmrand hinaus,
              die obersten Einträge (u. a. "Mail-Anfragen") waren nicht erreichbar */}
          <div className="absolute bottom-16 left-0 right-0 bg-card rounded-t-xl shadow-xl border-t p-4 space-y-1 overflow-y-auto overscroll-contain" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', maxHeight: 'calc(100dvh - 4rem - 0.5rem)' }} onClick={e => e.stopPropagation()} data-testid="mobile-mehr-liste">
            {moreItems.map(({ path, icon: Icon, label, action }) => action === "butler" ? (
              <button key={path} type="button" onClick={() => { butlerUmschalten(); setMoreOpen(false); }} aria-pressed={butlerAktiv} data-testid="mobile-more-butler"
                className={`flex items-center gap-3 px-4 py-3 w-full text-left rounded-sm ${butlerAktiv ? "bg-red-600 text-white" : "text-muted-foreground"}`}>
                <Icon className="w-5 h-5" /><span className="font-medium">{butlerAktiv ? "Butler beenden" : label}</span>
              </button>
            ) : (
              <Link key={path} to={path} onClick={() => setMoreOpen(false)} data-testid={`mobile-more-${path.slice(1)}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm ${location.pathname.startsWith(path) ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}>
                <Icon className="w-5 h-5" /><span className="font-medium">{label}</span>
                {path === "/module/mail-inbox" && mailAnfragenNeu > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-blue-600 text-white text-xs font-bold" data-testid="mobile-badge-module/mail-inbox">
                    {mailAnfragenNeu > 99 ? "99+" : mailAnfragenNeu}
                  </span>
                )}
              </Link>
            ))}
            <button onClick={handleLogoutClick}
              className="flex items-center gap-3 px-4 py-3 w-full text-destructive rounded-sm mt-2 border-t pt-4">
              <LogOut className="w-5 h-5" /><span className="font-medium">Abmelden</span>
            </button>
            <div className="border-t pt-2 mt-2">
              <ThemeToggle variant="mobile" />
            </div>
          </div>
        </div>
      )}

      {/* Backup Dialog für Mobile */}
      {showBackupDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !isCreatingBackup && setShowBackupDialog(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-primary" />
              Backup vor dem Abmelden?
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Möchten Sie vor dem Abmelden ein Backup Ihrer Daten erstellen? Dies sichert alle Anfragen, Kunden, Rechnungen und Einstellungen.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleBackupAndLogout}
                disabled={isCreatingBackup}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isCreatingBackup ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Erstelle Backup...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Backup & Abmelden
                  </>
                )}
              </button>
              <button
                onClick={handleLogoutWithoutBackup}
                disabled={isCreatingBackup}
                className="px-4 py-3 border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Ohne Backup abmelden
              </button>
              <button
                onClick={() => setShowBackupDialog(false)}
                disabled={isCreatingBackup}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      <GlobalAssistantSheet open={assistantOpen} onClose={() => { setAssistantOpen(false); setAssistantSnoozeContext(null); }} snoozeContext={assistantSnoozeContext} />
    </>
  );
};

export { Sidebar, MobileNav, getUserRole };
